import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ThemeToggle from "@/components/ThemeToggle";
import { ShieldCheck, Mic, QrCode, FileCheck2, Activity } from "lucide-react";

const DEMO = [
  ["Admin", "admin@pathtraq.com", "Admin123!"],
  ["Pathologist", "path@pathtraq.com", "Path123!"],
  ["Histotech", "tech@pathtraq.com", "Tech123!"],
  ["Supervisor", "super@pathtraq.com", "Super123!"],
  ["Viewer", "viewer@pathtraq.com", "Viewer123!"],
];

const CAPABILITIES = [
  { icon: Activity, title: "Specimen tracking", detail: "Accession to sign-out, with turnaround on every case" },
  { icon: QrCode, title: "QR on every block and slide", detail: "Exact-size labels and a full chain of custody" },
  { icon: Mic, title: "Dictation and voice", detail: "Narrative fields you can speak, and Silas answers out loud" },
  { icon: FileCheck2, title: "CAP-ready records", detail: "SOPs, reagents, equipment and a complete audit trail" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [labLogo, setLabLogo] = useState(null);

  useEffect(() => {
    api.get("/lab/logo", { responseType: "blob" })
      .then((r) => setLabLogo(URL.createObjectURL(r.data)))
      .catch(() => setLabLogo(null));
  }, []);

  const signIn = async (mail, pwd) => {
    setBusy(true);
    setError("");
    const res = await login(mail.trim(), pwd);
    setBusy(false);
    if (res.ok) navigate("/hub");
    else setError(res.error);
  };

  return (
    <div className="grid min-h-screen bg-slate-950 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]"
         data-testid="login-page">
      {/* ---------- form side ---------- */}
      <div className="relative flex flex-col justify-between border-slate-800 px-5 py-8 sm:px-12 lg:border-r">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3" data-testid="login-brand">
            <img src="/pathqity-logo.png" alt="PathQity" data-testid="login-pathqity-logo"
                 onError={(e) => { e.currentTarget.style.display = "none"; }}
                 className="h-10 w-auto max-w-[220px] object-contain" />
            {labLogo && (
              <>
                <span className="h-7 w-px bg-slate-700" aria-hidden="true" />
                <img src={labLogo} alt="Lab logo" data-testid="login-lab-logo"
                     className="h-8 w-auto max-w-[170px] object-contain" />
              </>
            )}
          </div>
          <ThemeToggle className="rounded-md p-2 text-slate-400 hover:bg-white/5 hover:text-teal-300" />
        </div>

        <div className="mx-auto w-full max-w-md py-10">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-400">TraX Suite</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-50 sm:text-5xl">
            Precision pathology<br />management.
          </h1>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
            Cases, specimens, SOPs, reagents and equipment — tracked in one place, with the audit
            trail an inspector asks for.
          </p>

          <form onSubmit={(e) => { e.preventDefault(); signIn(email, password); }}
                className="mt-9 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                Email
              </Label>
              <Input id="email" type="email" autoComplete="username" required value={email}
                     onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input"
                     placeholder="you@lab.org"
                     className="mt-1.5 h-12 border-slate-700 bg-slate-900/80 font-mono text-sm text-slate-100 placeholder:text-slate-600 focus-visible:border-teal-400 focus-visible:ring-2 focus-visible:ring-teal-400/40" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                Password
              </Label>
              <Input id="password" type="password" autoComplete="current-password" required value={password}
                     onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input"
                     placeholder="••••••••"
                     className="mt-1.5 h-12 border-slate-700 bg-slate-900/80 font-mono text-sm text-slate-100 placeholder:text-slate-600 focus-visible:border-teal-400 focus-visible:ring-2 focus-visible:ring-teal-400/40" />
            </div>
            {error && (
              <div className="flex items-start gap-2 border-l-4 border-red-500 bg-red-950/40 px-3 py-2 text-sm text-red-300"
                   data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={busy} data-testid="login-submit-button"
                    className="h-12 w-full bg-teal-500 text-base font-bold text-slate-950 transition-all hover:bg-teal-400 hover:shadow-[0_0_28px_-6px_rgba(45,212,191,0.6)] disabled:opacity-60">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 border border-slate-800 bg-slate-900/60 p-4" data-testid="login-demo-accounts">
            <div className="mb-2 flex items-center gap-2 border-b border-slate-800 pb-2 text-[11px] font-bold uppercase tracking-wider text-teal-400">
              <ShieldCheck size={13} /> Pilot accounts — one click
            </div>
            <div className="grid gap-0.5">
              {DEMO.map(([role, mail, pwd]) => (
                <button key={mail} type="button" data-testid={`demo-login-${role.toLowerCase()}`}
                        onClick={() => { setEmail(mail); setPassword(pwd); signIn(mail, pwd); }}
                        className="group flex items-center justify-between gap-2 rounded px-2 py-2 text-left text-xs transition-colors hover:bg-teal-500/10">
                  <span className="font-semibold text-slate-200 group-hover:text-teal-300">{role}</span>
                  <span className="font-mono text-slate-500">{mail}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-600 lg:text-left">
          PathQity TraX Suite · role-based access · every action audited
        </p>
      </div>

      {/* ---------- credibility side ---------- */}
      <div className="relative hidden overflow-hidden lg:block">
        <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_pathlab-hub-20/artifacts/nofasedo_IMG_0015.jpeg"
             alt="Graham Health System pathology slides"
             data-testid="login-backdrop-image"
             className="h-full w-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-slate-950/20" />
        <div className="absolute inset-0 bg-[#134e4a]/10" />
        <div className="pointer-events-none absolute left-1/3 top-1/4 h-96 w-96 rounded-full bg-teal-500/10 blur-[120px]" />

        <div className="absolute right-8 top-8 flex items-center gap-3 border border-white/15 bg-slate-950/60 p-2 pr-4 backdrop-blur-xl"
             data-testid="login-graham-badge">
          <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_pathlab-hub-20/artifacts/dl1lrn8l_graham-health-system-1000-x-667.jpg"
               alt="Graham Health System" className="h-12 w-20 object-cover" />
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-200">Graham Health System</div>
        </div>

        <div className="absolute inset-0 flex flex-col justify-end gap-6 p-12">
          <div>
            <div className="font-display text-3xl font-bold text-white">Built for the grossing bench.</div>
            <p className="mt-2 max-w-lg text-sm leading-relaxed text-slate-300">
              Gloves on, hands busy. Scan a cassette to advance a case, dictate the gross, print
              labels at true size, and ask out loud what is running late.
            </p>
          </div>

          <div className="grid max-w-xl grid-cols-2 gap-3">
            {CAPABILITIES.map(({ icon: Icon, title, detail }) => (
              <div key={title} className="border-l-2 border-teal-500/60 bg-slate-900/60 p-3 backdrop-blur-sm">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-teal-300">
                  <Icon size={13} /> {title}
                </div>
                <p className="mt-1 text-xs leading-relaxed text-slate-400">{detail}</p>
              </div>
            ))}
          </div>

          <div className="max-w-xl border border-slate-700/80 bg-slate-900/80 p-4 backdrop-blur-xl">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-teal-300">
              <Mic size={12} /> Silas · voice assistant
            </div>
            <p className="mt-2 font-mono text-sm text-slate-200">
              “Silas, which cases are over turnaround?”
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Answers from your data, never from a model's guess — and reads it back out loud.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
